import React, { useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Grid,
} from "@mui/material";

const NewInvoicePrintTemplate = () => {
  const componentRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();

  const { data = {}, tableData = [], navigateBack } = location.state || {};
  const decimalCount = useSelector((state) => state.common?.decimalCount) || 2;

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => {
      navigate(navigateBack || -1);
    },
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handlePrint();
    }, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const themeColor = "#1a4675"; // Dark blue matching the image

  const getImageUrl = (prod) => {
    let rawImg = prod.photo || prod.image || prod.image_url || "";
    if (!rawImg) return "";

    try {
      // If it's a JSON string, parse it to extract the 'img' property
      if (typeof rawImg === "string" && rawImg.trim().startsWith("{")) {
        const parsed = JSON.parse(rawImg);
        return parsed.img || rawImg;
      }
    } catch (error) {
      console.log("Error parsing image JSON:", error);
    }

    return rawImg;
  };

  const getBatchValue = (batchVal) => {
    if (!batchVal) return "";
    try {
      if (typeof batchVal === "string" && batchVal.trim().startsWith("[")) {
        const parsed = JSON.parse(batchVal);
        if (Array.isArray(parsed)) {
          return parsed.join(", ");
        }
      }
    } catch (error) {
      console.log("Error parsing batch JSON:", error);
    }
    return batchVal;
  };

  const borderStyle = "1px solid #757575";

  const ITEMS_PER_PAGE = 7;
  const chunkedData = [];
  for (let i = 0; i < tableData.length; i += ITEMS_PER_PAGE) {
    chunkedData.push(tableData.slice(i, i + ITEMS_PER_PAGE));
  }
  if (chunkedData.length === 0) {
    chunkedData.push([]);
  }

  const leftSideRows = [
    {
      label: "Cash Part",
      value: data.CessP || 0,
    },
    {
      label: "Cheque Part",
      value: data.Cess || 0,
    },
    {
      label: "Opening Balance",
      value: data.BalanceAmount || 0,
    },
    { label: "New Bill", value: data.BillNetTotalField || 0 },
    {
      label: "Closing Balance",
      value:
        (Number(data.BalanceAmount) || 0) +
        (Number(data.BillNetTotalField) || 0) -
        (Number(data.advance) || 0),
    },
  ].filter((row) => Number(row.value) > 0);

  const rightSideRows = [
    {
      label: "SUB TOTAL",
      value: data.BillTotalField || 0,
      alwaysShow: true,
    },
    {
      label: data?.BillDiscPercField
        ? `DISCOUNT (${data.BillDiscPercField}%)`
        : "DISCOUNT",
      value: data.BillDiscAmtField || 0,
    },
    {
      label: "GST",
      value: data.BillPackageField || 0,
    },
    {
      label: "NET TOTAL",
      value: data.BillNetTotalField || 0,
      alwaysShow: true,
    },
  ].filter((row) => row.alwaysShow || Number(row.value) > 0);

  return (
    <Box sx={{ display: "none" }}>
      <Box ref={componentRef}>
        {chunkedData.map((pageData, pageIndex) => (
          <Box
            key={pageIndex}
            sx={{
              boxSizing: "border-box",
              p: "20px",
              fontFamily: "'Roboto', sans-serif",
              color: "black",
              width: "100%",
              maxWidth: "210mm",
              minHeight: "270mm",
              display: "flex",
              flexDirection: "column",
              m: "0 auto",
              bgcolor: "white",
              pageBreakAfter:
                pageIndex < chunkedData.length - 1 ? "always" : "auto",
            }}
          >
            {/* Header Section */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Box sx={{ width: "25%" }}>
                <Typography
                  sx={{
                    fontSize: "36px",
                    fontWeight: "900",
                    color: themeColor,
                    m: 0,
                    lineHeight: 1,
                  }}
                >
                  ESTIMATE
                </Typography>
              </Box>

              <Box sx={{ width: "50%", textAlign: "center", pt: "20px" }}>
                {/* Empty middle space for header */}
              </Box>

              <Box
                sx={{
                  width: "25%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                }}
              >
                <Table
                  size="small"
                  sx={{
                    width: "200px",
                    border: borderStyle,
                    "& .MuiTableCell-root": {
                      border: borderStyle,
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "13px",
                      p: "2px",
                      color: "black",
                    },
                  }}
                >
                  <TableBody>
                    <TableRow>
                      <TableCell>EST.NO</TableCell>
                      <TableCell>DATE</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{data.BillNo}</TableCell>
                      <TableCell>
                        {data.BillDate
                          ? dayjs(data.BillDate).format("DD-MM-YYYY")
                          : ""}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Stack>

            {/* Customer Info Bar */}
            <Box
              sx={{
                bgcolor: themeColor,
                color: "white",
                p: "4px 8px",
                fontWeight: "bold",
                fontSize: "14px",
                mt: "5px",
                width: "100%",
                textTransform: "uppercase",
              }}
            >
              CUSTOMER INFO
            </Box>

            {/* Customer Info Text (Under the bar) */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                pt: "5px",
                pb: "5px",
              }}
            >
              <Box sx={{ textAlign: "left" }}>
                <Typography sx={{ fontWeight: "900", fontSize: "16px" }}>
                  {data.BillPartyName === "3"
                    ? "CASH"
                    : data.BillPartyName === "4"
                      ? "CASH PURCHASE"
                      : data.BillPartyName}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: "900",
                    fontSize: "16px",
                    textTransform: "uppercase",
                  }}
                >
                  {data.Address1
                    ? `${data.Address1} ${data.Address2 || ""}`
                    : ""}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography sx={{ fontWeight: "900", fontSize: "16px" }}>
                  {data.Ph ? `PH: ${data.Ph}` : ""}
                </Typography>
              </Box>
            </Box>

            {/* Items Table */}
            <TableContainer
              sx={{
                mt: "15px",
                border: borderStyle,
                borderBottom: "none",
                overflow: "visible",
              }}
            >
              <Table
                sx={{
                  "& .MuiTableCell-root": {
                    border: borderStyle,
                    p: "4px 5px",
                    fontSize: "13px",
                    color: "black",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {[
                      { label: "Sl no", width: "4%" },
                      { label: "Model No", width: "25%" },
                      { label: "Color code", width: "10%" },
                      { label: "PHOTO", width: "22%" },
                      { label: "Description", width: "25%" },
                      { label: "Qty", width: "7%" },
                      { label: "Unit Price", width: "10%" },
                      { label: "Amount", width: "12%" },
                    ].map((col, idx) => (
                      <TableCell
                        key={idx}
                        sx={{
                          bgcolor: themeColor,
                          color: "white !important",
                          fontWeight: "bold",
                          textAlign: "center",
                          width: col.width,
                          lineHeight: 1.2,
                        }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageData.map((prod, index) => {
                    const photoUrl = getImageUrl(prod);
                    const actualIndex = pageIndex * ITEMS_PER_PAGE + index + 1;
                    return (
                      <TableRow key={index}>
                        <TableCell align="center">{actualIndex}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: "bold" }}>
                          {prod.subCategory || ""}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: "bold" }}>
                          {prod.ItemC || prod.model_no || ""}
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              width: "110px",
                              height: "80px",
                              m: "0 auto",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                            }}
                          >
                            {photoUrl ? (
                              <img
                                src={photoUrl}
                                alt="Product"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              <Typography
                                sx={{ color: "#ccc", fontSize: "12px" }}
                              >
                                No Image
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontWeight: "bold",
                            textTransform: "uppercase",
                          }}
                        >
                          {prod.ItemNameTextField || prod.description || ""}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: "bold" }}>
                          {prod.qty}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: "bold" }}>
                          {(Number(prod.Rate1) || 0).toFixed(decimalCount)}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: "bold" }}>
                          {(Number(prod.total) || 0).toFixed(decimalCount)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ flexGrow: 1 }} />

            {/* Terms and conditions */}
            {pageIndex === chunkedData.length - 1 &&
              localStorage.getItem("invoiceTermsCondition") && (
                <Box
                  sx={{
                    border: borderStyle,
                    borderBottom: "none",
                    p: "4px",
                    "& .ql-font-serif": { fontFamily: "serif" },
                    "& .ql-align-justify": { textAlign: "justify" },
                    "& .ql-align-center": { textAlign: "center" },
                    "& .ql-align-right": { textAlign: "right" },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: localStorage.getItem("invoiceTermsCondition"),
                  }}
                />
              )}

            {/* Footer block - exactly aligning with the table columns */}
            {pageIndex === chunkedData.length - 1 && (
              <Box sx={{ border: borderStyle, marginTop: "auto" }}>
                <Grid container alignItems="stretch">
                  {/* Left side balances */}
                  <Grid
                    item
                    xs={8}
                    sx={{
                      borderRight: borderStyle,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {leftSideRows.map((row, idx, arr) => (
                      <Box
                        key={idx}
                        sx={{
                          flex: 1,
                          display: "flex",
                          minHeight: "33px",
                          borderBottom:
                            idx !== arr.length - 1 ? borderStyle : "none",
                        }}
                      >
                        <Box
                          sx={{
                            width: "50%",
                            p: "6px",
                            fontWeight: "bold",
                            textAlign: "center",
                            fontSize: "14px",
                            color:
                              row.label === "Cash Part" ||
                              row.label === "Cheque Part"
                                ? "red"
                                : "inherit",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {row.label}
                        </Box>
                        <Box
                          sx={{
                            width: "50%",
                            p: "6px",
                            fontWeight: "bold",
                            textAlign: "center",
                            fontSize: "14px",
                            color:
                              row.label === "Cash Part" ||
                              row.label === "Cheque Part"
                                ? "red"
                                : "inherit",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {Number(row.value || 0).toFixed(decimalCount)}
                        </Box>
                      </Box>
                    ))}
                  </Grid>

                  {/* Right side totals */}
                  <Grid
                    item
                    xs={4}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {rightSideRows.map((row, idx, arr) => (
                      <Box
                        key={idx}
                        sx={{
                          flex: 1,
                          display: "flex",
                          minHeight: "33px",
                          borderBottom:
                            idx !== arr.length - 1 ? borderStyle : "none",
                        }}
                      >
                        <Box
                          sx={{
                            width: "50%",
                            p: "6px",
                            fontWeight: "bold",
                            textAlign: "center",
                            fontSize: "14px",
                            borderRight: borderStyle,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {row.label}
                        </Box>
                        <Box
                          sx={{
                            width: "50%",
                            p: "6px",
                            fontWeight: "bold",
                            textAlign: "center",
                            fontSize: "14px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {Number(row.value || 0).toFixed(decimalCount)}
                        </Box>
                      </Box>
                    ))}
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default NewInvoicePrintTemplate;
